/**
 * mapAnalytics
 *
 * Thin wrapper around POST /api/map/analytics/event for top-funnel
 * event tracking (pin_click | nav_click | maps_click).
 *
 * Guarantees:
 * - Fire-and-forget (never blocks UI).
 * - Dedupe: suppress duplicate events within 1000 ms for the same
 *   (session_id + event_type + label + path) tuple.
 * - Automatic context enrichment: path, lang, device, session id.
 * - No PII beyond ids.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEDUPE_WINDOW_MS = 1000;

// In-memory dedupe cache: Map<key, lastTs>
const _lastFired = new Map();

const getSessionId = () => {
  try {
    let sid = localStorage.getItem('anon_session_id');
    if (!sid) {
      sid = `anon_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
      localStorage.setItem('anon_session_id', sid);
    }
    return sid;
  } catch (_) {
    return null;
  }
};

const getDevice = () => {
  if (typeof window === 'undefined') return undefined;
  return window.matchMedia && window.matchMedia('(max-width: 768px)').matches
    ? 'mobile'
    : 'desktop';
};

const getLang = () => {
  try {
    return (
      localStorage.getItem('preferredLanguage') ||
      (typeof document !== 'undefined' ? document.documentElement.lang : null) ||
      undefined
    );
  } catch (_) {
    return undefined;
  }
};

export function trackMapEvent(eventType, { label, providerId, userId, role } = {}) {
  if (!['pin_click', 'nav_click', 'maps_click', 'demo_open',
        'live_demo_cta_click', 'live_demo_opened'].includes(eventType)) {
    // eslint-disable-next-line no-console
    console.warn('[mapAnalytics] unknown event_type:', eventType);
    return;
  }
  if (typeof window === 'undefined') return;

  const sessionId = getSessionId();
  const path = window.location.pathname;
  const key = `${sessionId || 'anon'}|${eventType}|${label || ''}|${path}`;
  const now = Date.now();
  const last = _lastFired.get(key) || 0;
  if (now - last < DEDUPE_WINDOW_MS) {
    return; // deduped
  }
  _lastFired.set(key, now);

  const payload = {
    event_type: eventType,
    session_id: sessionId,
    user_id: userId,
    role: role || 'guest',
    path,
    label,
    lang: getLang(),
    device: getDevice(),
    user_agent: navigator?.userAgent,
    provider_id: providerId,
  };

  axios
    .post(`${BACKEND_URL}/api/map/analytics/event`, payload, { timeout: 4000 })
    .catch(() => {
      /* analytics must never break the UI */
    });
}

export default trackMapEvent;
